import { Alert } from '@/global/components/Alert'
import IconButton from '@/global/components/Button/IconButton'
import IconeAjuda from 'global/components/IconeAjuda'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { DownloadIcon } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { clientes, funcionario } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import * as XLSX from 'xlsx'
import { useImportacaoStore } from '../../store'
import { ImportacaoSchema } from '../../store/types'
import FormImportar from '../forms/Importacao/FormImportar'

export default function ImportarFuncionario() {
    const { data: limites } = clientes.limites.useFetch(false)
    const navigate = useNavigate()

    const { setErro, setIsLoading, reset, setImports } = useImportacaoStore(
        store => store.actions,
    )

    const toggle = () => {
        reset()
        navigate('/cadastros/funcionarios')
    }

    const {
        funcionariosImportar = [],
        linhaInicial,
        isLoading,
        Cidade,
    } = useImportacaoStore(store => store.state)

    const podeCadastrar =
        Number(limites?.ativos) + funcionariosImportar.length <
        Number(limites?.limite)

    const { mutateAsync } = funcionario.mutations.useCreate({
        invalidateQuery: true,
        onSuccess: async data => {
            await funcionario.invalidateQueries()
            toast.success('Funcionário importado com sucesso')
            setImports(prev =>
                prev?.map(funcionario => {
                    if (funcionario && funcionario.matricula === data.matricula)
                        return {
                            ...funcionario,
                            importado: true,
                            erro: false,
                        }
                    else return funcionario
                }),
            )
        },
        onError: (error, { matricula }: any) => {
            setErro(
                e =>
                    (e ? e + '\n' + error.message : error.message) +
                    ` (linha ${
                        funcionariosImportar?.findIndex(
                            f => f && f.matricula === matricula,
                        )! +
                        1 +
                        linhaInicial
                    })`,
            )
            setImports(prev =>
                prev?.map(funcionario => {
                    if (
                        funcionario !== undefined &&
                        funcionario.matricula === matricula
                    )
                        return {
                            ...funcionario,
                            erro: true,
                            importado: false,
                        }
                    return funcionario
                }),
            )
        },
    })
    const empresaId = useStore(state => state.companyId)

    const handleSubmit = async (values: any) => {
        const paraImportar = funcionariosImportar?.filter(
            f => f && !f.erro && !f.importado,
        )
        if (!paraImportar?.length)
            return setErro('Nenhum funcionário para importar')

        setErro(null)
        setIsLoading(true)
        for await (const funcionario of paraImportar) {
            const payload = {
                ...funcionario,
                ...{
                    matricula: funcionario?.matricula
                        ? Number(funcionario.matricula)
                        : undefined,
                    reconhecimentoFacial: funcionario?.reconhecimentoFacial
                        ? Number(funcionario.reconhecimentoFacial)
                        : undefined,
                    funcao: funcionario?.cargo,
                },
                Cidade,
                empresaId,
                horarioNoturno: 'N',
                regraCalculo: Number(values.regraCalculo),
                tabelaHorarioId: Number(values.tabelaHorarioId),
            } as any
            delete payload.id
            if (!payload.matricula) {
                delete payload.matricula
            }
            if (!payload.reconhecimentoFacial) {
                delete payload.reconhecimentoFacial
            }

            await mutateAsync(payload).catch(e => {
                if (
                    String(e.message).trim() ===
                    'Você atingiu o limite de funcionários para sua conta'
                )
                    throw e
            })
        }
        setIsLoading(false)
        setErro(prev => {
            if (!prev) {
                toast.success(
                    'Todos os funcionários foram importados com sucesso',
                )
                toggle()
            }
            return prev
        })
    }

    return (
        <Dialog.Root
            loading={isLoading}
            onClose={toggle}
            onSubmit={v =>
                handleSubmit(v).catch(e => {
                    console.error(e)
                    setIsLoading(false)
                })
            }
            schema={ImportacaoSchema}
            open={hasPermission(['read', 'ImportarFuncionários'])}
            disableFields={!hasPermission(['create', 'Funcionários'])}
        >
            <Dialog.Title>
                Importar Funcionários
                <div style={{ display: 'flex' }}>
                    <IconeAjuda>
                        <h4 style={{ textAlign: 'center' }}>
                            Importação XLSX{' '}
                            <IconButton
                                title="Baixar arquivo de exemplo"
                                onClick={() => baixarExemplo('xlsx')}
                            >
                                <DownloadIcon />
                            </IconButton>
                        </h4>
                        <br />O arquivo deve ser uma planilha com as seguintes
                        colunas:
                        <li>A - Matrícula</li>
                        <li>B - Nome</li>
                        <li>C - Cargo/Função</li>
                        <li>D - PIS/PASEP</li>
                        <li>E - CPF</li>
                        <li>F - Data de admissão</li>
                        <br />A <strong>primeira linha</strong> do arquivo é
                        composta pelo cabeçalho e portanto{' '}
                        <strong>será ignorada</strong>. Além disso, o campo Data
                        de admissão deve estar em um dos seguintes formatos:
                        <li>DD/MM/YY</li>
                        <li>DD-MM-YYYY</li>
                        <li>'DD/MM/YYYY</li>
                        <li>YYYY-MM-DD</li>
                        <li>YYYYMMDD</li>
                        <br /> <br />
                        <h4 style={{ textAlign: 'center' }}>
                            Importação CSV{' '}
                            <IconButton
                                title="Baixar arquivo de exemplo"
                                onClick={() => baixarExemplo('csv')}
                            >
                                <DownloadIcon />
                            </IconButton>
                        </h4>
                        <br />O arquivo deve conter as seguintes informações:
                        <br />
                        <br />
                        matricula;nome;funcao;pis;cpf;dtAdmissao
                    </IconeAjuda>
                </div>
            </Dialog.Title>
            <Dialog.Content dividers>
                {!podeCadastrar && (
                    <Alert severity="warning" className="mb-4">
                        O limite de funcionários para o seu plano é de{' '}
                        {limites?.limite}. Para adicionar mais,&nbsp;
                        <Link to="/planos/me">altere seu plano</Link>.
                    </Alert>
                )}
                {hasPermission(
                    ['create', 'Funcionários'],
                    () => null,
                    () => (
                        <Alert severity="error">
                            Você precisa da permissão de criar funcionários para
                            importar funcionários.
                        </Alert>
                    ),
                )}
                <FormImportar />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                {hasPermission(['create', 'Funcionários'], () => (
                    <Dialog.Actions.PrimaryButton disabled={!podeCadastrar}>
                        {funcionariosImportar?.find(f => f && f.erro)
                            ? 'Importar funcionários válidos'
                            : 'Importar todos'}
                    </Dialog.Actions.PrimaryButton>
                ))}
            </Dialog.Actions>
        </Dialog.Root>
    )
}

function baixarExemplo(formato: 'xlsx' | 'csv') {
    const data = [
        [
            '123456',
            'João da Silva',
            'Desenvolvedor',
            '123.45678.90-1',
            '791.268.010-19',
            '01/01/2020',
        ],
        [
            '789012',
            'Maria Oliveira',
            'Gerente',
            '98765432101',
            '98765432100',
            '01/02/2020',
        ],
    ]

    if (formato === 'xlsx') {
        const ws = XLSX.utils.aoa_to_sheet([
            [
                'Matrícula',
                'Nome do funcionário',
                'Cargo/Função',
                'PIS/PASEP',
                'CPF',
                'Data de admissão',
            ],
            ...data,
        ])

        ws['!cols'] = [
            { wpx: 60 },
            { wpx: 200 },
            { wpx: 75 },
            { wpx: 80 },
            { wpx: 80 },
            { wpx: 100 },
        ]

        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'sheet1')
        XLSX.writeFile(wb, 'exemplo-importacao-funcionarios.xlsx')
    } else if (formato === 'csv') {
        const content = [
            ['matricula', 'nome', 'funcao', 'pis', 'cpf', 'dtAdmissao'],
            ...data,
        ]
            .map(row => row.join(';'))
            .join('\n')
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'exemplo-importacao-funcionarios.csv'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
    }
}
